/**
 * BroadCastForm コンポーネント
 *
 * 配信作成・編集画面のメインコンポーネント。
 * 通常配信とセグメント配信の2つの配信タイプを選択でき、
 * それぞれ専用のフォームコンポーネントを表示する。
 * 編集モードでは配信タイプの変更は不可。
 *
 * @file 配信フォームメイン
 * @date 2025/09/18
 */

import React, { useState } from 'react';
import { Card, Radio, Form } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { BroadCastFormType } from 'src/models/broadCast';
import { NormalBroadCastForm } from './normalForm';
import { SegmentBroadCastForm } from './segmentForm';

/**
 * Props インターフェース
 */
interface Prop {
  /** 編集時の初期値 */
  init?: BroadCastFormType;
}

/**
 * 配信タイプの定義
 */
type DeliveryType = 'normal' | 'segment';

/**
 * 型ガード: 値が有効なDeliveryTypeかを判定
 * 
 * @param type - チェック対象の値
 * @returns DeliveryType型であればtrue
 */
const isValidDeliveryType = (type: unknown): type is DeliveryType => {
  return type === 'normal' || type === 'segment';
};

/**
 * BroadCastForm コンポーネント
 *
 * 配信タイプ（通常配信/セグメント配信）を選択し、
 * 対応するフォームコンポーネントを表示する。
 * 編集モードでは配信タイプは固定される。
 *
 * @param props - コンポーネントのプロパティ
 * @returns JSX.Element
 */
export const BroadCastForm: React.FC<Prop> = ({ init }) => {
  /**
   * 配信タイプの状態管理
   * 編集モードの場合は既存データから、新規作成の場合は通常配信をデフォルトとする
   */
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<DeliveryType>(() => {
    // 編集モードの場合、既存データから配信タイプを判定
    if (init?.broadCast?.deliveryType && isValidDeliveryType(init.broadCast.deliveryType)) {
      return init.broadCast.deliveryType;
    }
    // 新規作成の場合はデフォルトで通常配信
    return 'normal';
  });

  /**
   * 編集モードかどうかの判定
   * 既存のbroadCastデータが存在する場合は編集モード
   */
  const isEditMode = !!init?.broadCast;

  /**
   * 配信タイプ変更時の処理
   * 新規作成時のみ配信タイプの変更が可能
   *
   * @param e - ラジオボタンのchangeイベント
   */
  const onDeliveryTypeChange = (e: RadioChangeEvent) => {
    const newType = e.target.value as DeliveryType;
    setSelectedDeliveryType(newType);
  };

  return (
    <Card style={{ paddingTop: '3%' }}>
      <Form
        labelWrap
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16, offset: 1 }}
        layout="horizontal"
        style={{ maxWidth: 900, margin: 'auto' }}
      >
        <Form.Item
          label="配信タイプ"
          required
          tooltip="編集中は配信タイプの変更はできません"
          style={{ marginBottom: 24 }}
        >
          <Radio.Group
            value={selectedDeliveryType}
            onChange={onDeliveryTypeChange}
            disabled={isEditMode}
            aria-label="配信タイプの選択"
            aria-describedby={isEditMode ? "delivery-type-edit-hint" : undefined}
          >
            <Radio value="normal">通常配信</Radio>
            <Radio value="segment">セグメント配信</Radio>
          </Radio.Group>
          {isEditMode && (
            <span id="delivery-type-edit-hint" style={{ display: 'none' }}>
              編集中は配信タイプの変更はできません
            </span>
          )}
        </Form.Item>
      </Form>

      {selectedDeliveryType === 'normal' ? (
        <NormalBroadCastForm 
          init={init} 
          deliveryTypeChanged={!isEditMode}
        />
      ) : (
        <SegmentBroadCastForm 
          init={init} 
          deliveryTypeChanged={!isEditMode}
        />
      )}
    </Card>
  );
};
